import AddLocationIcon from '@mui/icons-material/AddLocation'
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter'
import HomeIcon from '@mui/icons-material/Home'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import SchoolIcon from '@mui/icons-material/School'
import StarIcon from '@mui/icons-material/Star'
import WorkIcon from '@mui/icons-material/Work'
import { Box, IconButton, List, ListItem, ListItemButton, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router'
import maplibregl from 'maplibre-gl'
import { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SearchSelect } from 'src/components/SearchSelect'
import AutomaticSheet from 'src/components/sheet/AutomaticSheet'
import { MapRenderContext } from 'src/contexts/MapRenderContext'
import { POI, addPoi, updatePoi } from 'src/lib/pois'

export const Route = createFileRoute('/plan/poi/')({
    component: PoiEditor,
})

const ICON_LIST = [
    { name: 'home', component: <HomeIcon />, defaultNameKey: 'Home' },
    { name: 'work', component: <WorkIcon />, defaultNameKey: 'Work' },
    { name: 'school', component: <SchoolIcon />, defaultNameKey: 'School' },
    { name: 'workout', component: <FitnessCenterIcon />, defaultNameKey: 'Workout' },
    { name: 'star', component: <StarIcon />, defaultNameKey: 'Other' },
]

function PoiEditor() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const routerState = useRouterState()
    const { map } = useContext(MapRenderContext)

    const editingPoi = (routerState.location.state as unknown as { editingPoi?: POI })?.editingPoi as POI | undefined

    const [selectedIcon, setSelectedIcon] = useState(editingPoi?.icon ?? 'home')
    const [tab, setTab] = useState('map')
    const [poiName, setQuery] = useState(editingPoi?.name ?? '')
    const [marker, setMarker] = useState<maplibregl.Marker | null>(null)
    const [initialCenter, setInitialCenter] = useState<{ lat: number, lon: number } | null>(null)

    useEffect(() => {
        if (editingPoi) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setInitialCenter({ lat: editingPoi.lat, lon: editingPoi.lon })
        }
    }, [editingPoi])

    useEffect(() => {
        if (!map?.current) return
        const mapInstance = map.current
        const center = initialCenter ?? mapInstance.getCenter()
        const newMarker = new maplibregl.Marker().setLngLat(center).addTo(mapInstance)

        if (initialCenter) {
            mapInstance.flyTo({ center: [initialCenter.lon, initialCenter.lat], zoom: 15 })
        }

        const updateMarkerPosition = () => {
            const currentCenter = mapInstance!.getCenter()
            newMarker.setLngLat(currentCenter)
        }

        mapInstance.on('move', updateMarkerPosition)
        mapInstance.on('zoom', updateMarkerPosition)

        // eslint-disable-next-line react-hooks/set-state-in-effect
        setMarker(newMarker)

        return () => {
            newMarker.remove()
            mapInstance?.off('move', updateMarkerPosition)
            mapInstance?.off('zoom', updateMarkerPosition)
        }
    }, [map, initialCenter])

    const confirmPoi = () => {
        if (!map?.current || !marker) return

        const position = map.current.getCenter()

        if (editingPoi) {
            updatePoi(editingPoi.id, {
                name: poiName,
                icon: selectedIcon,
                lat: position.lat,
                lon: position.lng,
            })
        }
        else {
            addPoi({
                name: poiName,
                icon: selectedIcon,
                lat: position.lat,
                lon: position.lng,
            })
        }

        navigate({ to: '/plan' } as Parameters<typeof navigate>[0])
    }

    return (
        <AutomaticSheet>
            <List style={{ padding: 0, paddingTop: '4px', width: '100%' }}>
                <ListItem>
                    <ToggleButtonGroup
                        value={tab}
                        exclusive
                        size="small"
                        onChange={(_, v) => { if (v) setTab(v) }}
                        fullWidth
                        sx={{ justifyContent: 'center', color: 'primary.main' }}
                    >
                        <ToggleButton value="map">{t('Select on map')}</ToggleButton>
                        <ToggleButton value="search">{t('Search')}</ToggleButton>
                    </ToggleButtonGroup>
                </ListItem>
            </List>

            {tab === 'map' && (
                <List style={{ padding: 0, width: '100%' }}>
                    <ListItem>
                        <TextField
                            variant="outlined"
                            placeholder={t('Enter the name of the location')}
                            fullWidth
                            value={poiName}
                            onChange={event => setQuery(event.target.value)}
                            sx={{ marginBottom: '1rem' }}
                        />
                    </ListItem>
                    <ListItem>
                        <Typography variant="h6">{t('Select icon')}</Typography>
                    </ListItem>
                    <ListItem>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                            {ICON_LIST.map(icon => (
                                <IconButton
                                    key={icon.name}
                                    onClick={() => {
                                        const defaultName = t(icon.defaultNameKey)
                                        if (
                                            (poiName === '' && poiName !== defaultName)
                                            || ICON_LIST.some(i => t(i.defaultNameKey) === poiName)
                                        ) {
                                            setQuery(defaultName)
                                        }
                                        setSelectedIcon(icon.name)
                                    }}
                                    sx={{
                                        'backgroundColor': selectedIcon === icon.name ? 'primary.main' : 'transparent',
                                        'color': selectedIcon === icon.name ? 'white' : 'primary.main',
                                        '&:hover': {
                                            backgroundColor: 'primary.main',
                                            color: 'white',
                                        },
                                    }}
                                >
                                    {icon.component}
                                </IconButton>
                            ))}
                        </Box>
                    </ListItem>

                    <ListItemButton onClick={confirmPoi} sx={{ height: '4rem' }} disabled={poiName.length === 0}>
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                            <AddLocationIcon sx={{ color: 'primary.main' }} />
                            <Typography variant="body1">
                                {editingPoi ? t('Save') : t('Confirm selected location')}
                            </Typography>
                            <NavigateNextIcon sx={{ color: 'primary.main', marginLeft: 'auto' }} />
                        </Box>
                    </ListItemButton>
                </List>
            )}

            {tab === 'search' && (
                <SearchSelect onSelect={(result) => {
                    map?.current?.flyTo({
                        center: [result.location?.longitude ?? 0, result.location?.latitude ?? 0],
                        zoom: 15,
                    })
                    setTab('map')
                }}
                />
            )}
        </AutomaticSheet>
    )
}
